import PropTypes from 'prop-types';

/**
 * Select Component (Preline UI)
 *
 * A native HTML select dropdown with Preline/Tailwind styling.
 * Provides consistent select styling across the application with validation states.
 *
 * @param {Object} props
 * @param {string} [props.label] - Label text for the select
 * @param {Array} props.options - Array of {value, label} option objects (optional if using children)
 * @param {React.ReactNode} [props.children] - Option elements as children (alternative to options prop)
 * @param {string} [props.value] - Currently selected value
 * @param {Function} [props.onChange] - Change handler (receives event)
 * @param {string} [props.placeholder] - Placeholder text for empty option
 * @param {boolean} [props.disabled=false] - Whether select is disabled
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.required] - Whether the field is required
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.id] - HTML id attribute
 * @param {string} [props.name] - HTML name attribute
 */
export function Select({
  label,
  options = [],
  children,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  required = false,
  className = '',
  id,
  name,
  ariaLabel,
}) {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  const selectClasses = `
    py-3 px-4 pe-9 block w-full border-border-default rounded-lg text-sm
    focus:border-brand focus:ring-brand
    disabled:opacity-50 disabled:pointer-events-none
    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-xs font-medium text-muted mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <select
        id={selectId}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
        className={selectClasses}
      >
        {placeholder && (
          <option value="">
            {placeholder}
          </option>
        )}
        {/* Support both options prop and children */}
        {children || options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Error Message (Preline UI) */}
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

Select.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  children: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  ariaLabel: PropTypes.string,
};
